import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Copy, MessageCircle, Facebook, Twitter, Send, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_ORIGIN = process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin;

export default function ShareCard({ onCoinsAwarded }) {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/coins/share-info`);
      setInfo(res.data);
    } catch (err) {
      console.error("[ShareCard] load failed", err);
    }
  };
  useEffect(() => { load(); }, []);

  const referralLink = info ? `${APP_ORIGIN}?ref=${info.referral_code}` : "";
  const shareText = `Estou usando o FUTURO PDV — rede social + freelance + carona! Acesse: ${referralLink}`;

  const register = async (channel) => {
    try {
      const res = await axios.post(`${API}/coins/share`, { channel });
      if (res.data.awarded > 0) {
        toast.success(res.data.message);
        onCoinsAwarded?.(res.data.awarded);
      } else {
        toast.info(res.data.message);
      }
      load();
    } catch (err) {
      console.error("[ShareCard] register share failed", err);
    }
  };

  const share = async (channel, url) => {
    if (channel === "copy_link") {
      try {
        await navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("[ShareCard] clipboard write failed", err);
        toast.info(`Link: ${referralLink}`);
      }
    } else if (channel === "native" && navigator.share) {
      try {
        await navigator.share({ title: "FUTURO PDV", text: shareText, url: referralLink });
      } catch { return; /* user canceled - don't award */ }
    } else if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    await register(channel);
  };

  if (!info) return null;

  const progress = Math.min(100, (info.shares_today / info.daily_limit) * 100);

  return (
    <Card className="p-6 border-blue-500/30 bg-gradient-to-br from-blue-500/10 via-transparent to-primary/10" data-testid="share-card">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
          <Share2 className="w-6 h-6 text-primary-foreground" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold tracking-tight">Convide amigos, ganhe <span className="bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">FUTURO PDV</span></h3>
          <p className="text-xs text-muted-foreground">
            +{info.coins_per_share} FUTURO PDV por compartilhamento (até {info.daily_limit}/dia)
          </p>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1">
          <span className="font-semibold">Hoje</span>
          <span className="text-muted-foreground">{info.shares_today}/{info.daily_limit}</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="text-xs text-muted-foreground mt-1">Total: {info.total_shares} compartilhamentos</div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <Button
          data-testid="share-whatsapp"
          onClick={() => share("whatsapp", `https://wa.me/?text=${encodeURIComponent(shareText)}`)}
          variant="outline" size="sm" className="rounded-full gap-2"
        >
          <MessageCircle className="w-4 h-4 text-green-600" /> WhatsApp
        </Button>
        <Button
          data-testid="share-telegram"
          onClick={() => share("telegram", `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(shareText)}`)}
          variant="outline" size="sm" className="rounded-full gap-2"
        >
          <Send className="w-4 h-4 text-blue-500" /> Telegram
        </Button>
        <Button
          data-testid="share-facebook"
          onClick={() => share("facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`)}
          variant="outline" size="sm" className="rounded-full gap-2"
        >
          <Facebook className="w-4 h-4 text-blue-600" /> Facebook
        </Button>
        <Button
          data-testid="share-twitter"
          onClick={() => share("twitter", `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`)}
          variant="outline" size="sm" className="rounded-full gap-2"
        >
          <Twitter className="w-4 h-4 text-sky-500" /> X
        </Button>
      </div>

      <Button
        data-testid="share-copy-link"
        onClick={() => share("copy_link")}
        variant="secondary" size="sm" className="w-full rounded-full gap-2"
      >
        {copied ? <><Check className="w-4 h-4 text-green-600" /> Link copiado!</> : <><Copy className="w-4 h-4" /> Copiar link</>}
      </Button>

      <div className="mt-3 p-2 rounded-lg bg-muted/50 font-mono text-[11px] text-muted-foreground break-all">
        {referralLink}
      </div>
    </Card>
  );
}
